import {
    Bar,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    Tooltip,
    XAxis,
    YAxis,
    BarChart,
    ResponsiveContainer,
} from "recharts";

import Typography from "@/ui/atoms/Typography";

import LayoutFillParentCentered from "@/ui/layouts/LayoutFillParentCentered";

import type { ExtractionAnalytics } from "@/ui/organisms/dashboardTypes";
import { CHART_COLORS } from "@/ui/organisms/dashboardTypes";

import { createSortByNumberPropertyComparator } from "@/utils/sort";

import { months } from "../createViewDashboard";

const compareByValueDesc = createSortByNumberPropertyComparator(
    "value",
    "desc",
);

export const retrieveExtractionAnalyticsNodes = (
    extractionAnalytics: ExtractionAnalytics,
    currentMonth: number,
) => {
    const barChartData = [
        { name: "Total", value: extractionAnalytics.totalExtractions },
        {
            name: "Last 7d",
            value: extractionAnalytics.extractionsLast7d,
        },
        {
            name: "Last 24h",
            value: extractionAnalytics.extractionsLast24h,
        },
    ];
    const draftCompletedData = [
        {
            name: "Draft",
            value: extractionAnalytics.extractionsByStatus["draft"],
        },
        {
            name: "Completed",
            value: extractionAnalytics.extractionsByStatus["completed"],
        },
    ];
    const dealsByUser = Object.entries(extractionAnalytics.extractionsByUser)
        .map(([user, count]) => ({ name: user, value: count }))
        .toSorted(compareByValueDesc);
    const dealsByFile = Object.entries(extractionAnalytics.extractionsByFile)
        .map(([file, count]) => ({ name: file, value: count }))
        .toSorted(compareByValueDesc);
    const lineData = Array.from({ length: 12 }, (_, i) => {
        const monthIndex = (currentMonth - (11 - i) + 12) % 12;
        return {
            month: months[monthIndex],
            value: extractionAnalytics.extractionsOverTime[monthIndex + 1] ?? 0,
        };
    });

    return extractionAnalytics.totalExtractions > 0
        ? [
              {
                  element: (
                      <>
                          <Typography variant="h1">
                              Total extractions:{" "}
                              {extractionAnalytics.totalExtractions}
                          </Typography>
                          <Typography variant="h1">
                              Extractions last 24h:{" "}
                              {extractionAnalytics.extractionsLast24h}
                          </Typography>
                          <Typography variant="h1">
                              Extractions last 7d:{" "}
                              {extractionAnalytics.extractionsLast7d}
                          </Typography>
                      </>
                  ),
                  options: {
                      i: "Extraction Numbers",
                      w: 2,
                      h: 2,
                      x: 0,
                      y: 0,
                  },
              },
              {
                  element: (
                      <>
                          <Typography
                              variant="h1"
                              className="text-center"
                              style={{ height: "10%" }}
                          >
                              Recent Extractions
                          </Typography>
                          <ResponsiveContainer width="90%" height="80%">
                              <BarChart
                                  data={barChartData}
                                  width={500}
                                  height={300}
                              >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="name" />
                                  <YAxis allowDecimals={false} />
                                  <Tooltip />
                                  <Bar dataKey="value">
                                      {barChartData.map((_, index) => (
                                          <Cell
                                              key={`cell-${index}`}
                                              fill={
                                                  CHART_COLORS[
                                                      index %
                                                          CHART_COLORS.length
                                                  ]
                                              }
                                          />
                                      ))}
                                  </Bar>
                              </BarChart>
                          </ResponsiveContainer>
                      </>
                  ),
                  options: {
                      i: "Extraction Bar Chart",
                      w: 2,
                      h: 2,
                      x: 2,
                      y: 0,
                  },
              },
              {
                  element: (
                      <>
                          <Typography
                              variant="h1"
                              className="text-center"
                              style={{ height: "10%" }}
                          >
                              Extraction Status
                          </Typography>
                          <ResponsiveContainer width="90%" height="80%">
                              <PieChart>
                                  <Pie
                                      data={draftCompletedData}
                                      dataKey="value"
                                      nameKey="name"
                                      outerRadius={100}
                                      fill="#8884d8"
                                  >
                                      {draftCompletedData.map((_, index) => (
                                          <Cell
                                              key={`cell-${index}`}
                                              fill={
                                                  CHART_COLORS[
                                                      index %
                                                          CHART_COLORS.length
                                                  ]
                                              }
                                          />
                                      ))}
                                  </Pie>
                                  <Legend
                                      verticalAlign="bottom"
                                      height={36}
                                      align="right"
                                  />
                              </PieChart>
                          </ResponsiveContainer>
                      </>
                  ),
                  options: {
                      i: "Extraction Pie Chart",
                      w: 2,
                      h: 2,
                      x: 4,
                      y: 0,
                  },
              },
              {
                  element: (
                      <>
                          <Typography
                              variant="h1"
                              className="text-center"
                              style={{ height: "10%" }}
                          >
                              Extractions by User
                          </Typography>
                          <ResponsiveContainer width="90%" height="80%">
                              <BarChart
                                  data={dealsByUser}
                                  width={500}
                                  height={300}
                              >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="name" />
                                  <YAxis allowDecimals={false} />
                                  <Tooltip />
                                  <Bar dataKey="value">
                                      {dealsByUser.map((_, index) => (
                                          <Cell
                                              key={`cell-${index}`}
                                              fill={
                                                  CHART_COLORS[
                                                      index %
                                                          CHART_COLORS.length
                                                  ]
                                              }
                                          />
                                      ))}
                                  </Bar>
                              </BarChart>
                          </ResponsiveContainer>
                      </>
                  ),
                  options: {
                      i: "User Extraction Chart",
                      w: 3,
                      h: 2,
                      x: 0,
                      y: 2,
                  },
              },
              {
                  element: (
                      <>
                          <Typography
                              variant="h1"
                              className="text-center"
                              style={{ height: "10%" }}
                          >
                              Extractions by File
                          </Typography>
                          <ResponsiveContainer width="90%" height="80%">
                              <BarChart
                                  data={dealsByFile}
                                  width={500}
                                  height={300}
                              >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis
                                      dataKey="name"
                                      tickFormatter={(value: string) =>
                                          `${value.slice(0, 20)}...`
                                      }
                                  />
                                  <YAxis allowDecimals={false} />
                                  <Tooltip />
                                  <Bar dataKey="value">
                                      {dealsByFile.map((_, index) => (
                                          <Cell
                                              key={`cell-${index}`}
                                              fill={
                                                  CHART_COLORS[
                                                      index %
                                                          CHART_COLORS.length
                                                  ]
                                              }
                                          />
                                      ))}
                                  </Bar>
                              </BarChart>
                          </ResponsiveContainer>
                      </>
                  ),
                  options: {
                      i: "File Extraction Chart",
                      w: 3,
                      h: 2,
                      x: 3,
                      y: 2,
                  },
              },
              {
                  element: (
                      <>
                          <Typography
                              variant="h1"
                              className="text-center"
                              style={{ height: "10%" }}
                          >
                              Extractions over time
                          </Typography>
                          <ResponsiveContainer width="90%" height="80%">
                              <LineChart data={lineData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="month" />
                                  <YAxis />
                                  <Tooltip />
                                  <Line
                                      type="monotone"
                                      dataKey="value"
                                      stroke="#8884d8"
                                  />
                              </LineChart>
                          </ResponsiveContainer>
                      </>
                  ),
                  options: {
                      i: "User Deal Chart",
                      w: 6,
                      h: 2,
                      x: 0,
                      y: 4,
                  },
              },
          ]
        : [
              {
                  element: (
                      <LayoutFillParentCentered>
                          <Typography variant="h1">
                              No Extractions Found
                          </Typography>
                      </LayoutFillParentCentered>
                  ),
                  options: {
                      i: "No Extractions",
                      w: 100,
                      h: 2,
                      x: 0,
                      y: 0,
                      boxed: false,
                      resizable: false,
                      draggable: false,
                  },
              },
          ];
};
